import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

const PDF_OPTIONS = {
  isEvalSupported: false,
};

const PdfViewer = ({
  url,
  pageNumber,
  onPageChange,
  numPages,
  onLoadSuccess,
  fitToWidth,
  scale,
  pageRenderWidth,
  docReady,
}) => {
  const twRef = useRef(null);
  const computedWidth = useMemo(() => {
    if (!fitToWidth || !pageRenderWidth) return undefined;
    return Math.floor(pageRenderWidth);
  }, [fitToWidth, pageRenderWidth]);

  useEffect(() => {
    if (!docReady) return;
    if (fitToWidth) {
      const api = twRef.current;
      if (api && typeof api.resetTransform === 'function') {
        api.resetTransform();
      }
    }
  }, [fitToWidth, docReady]);

  return (
    <div className='dv-canvas'>
      <Document
        file={url}
        loading={<div className='dv-loading'>Loading document…</div>}
        error={<div className='dv-error'>Failed to load PDF.</div>}
        options={PDF_OPTIONS}
        onLoadSuccess={onLoadSuccess}
      >
        {docReady && (
          <TransformWrapper
            ref={twRef}
            disablePadding
            smooth
            wheel={{ step: 0.2 }}
            pinch={{ step: 0.08 }}
            doubleClick={{ disabled: false, mode: 'zoomIn' }}
            panning={{
              velocityDisabled: false,
              disabled: false,
            }}
            minScale={0.3}
            maxScale={4}
            limitToBounds={false}
            centerOnInit={false}
            velocityAnimation={{
              sensitivity: 1,
              animationTime: 400,
              animationType: 'easeOut',
            }}
          >
            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%',
              }}
              contentStyle={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Page
                pageNumber={pageNumber}
                width={computedWidth}
                scale={fitToWidth ? 1 : scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </TransformComponent>
          </TransformWrapper>
        )}
      </Document>
      {docReady && numPages && numPages > 1 && (
        <div className='dv-pagination'>
          <button
            type='button'
            className='dv-icon-button'
            title='Previous page'
            disabled={pageNumber <= 1}
            onClick={() => onPageChange(Math.max(1, pageNumber - 1))}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className='dv-page-label'>
            {pageNumber} / {numPages}
          </span>
          <button
            type='button'
            className='dv-icon-button'
            title='Next page'
            disabled={pageNumber >= numPages}
            onClick={() => onPageChange(Math.min(numPages, pageNumber + 1))}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
};

PdfViewer.propTypes = {
  url: PropTypes.string.isRequired,
  pageNumber: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  numPages: PropTypes.number,
  onLoadSuccess: PropTypes.func.isRequired,
  fitToWidth: PropTypes.bool.isRequired,
  scale: PropTypes.number.isRequired,
  pageRenderWidth: PropTypes.number,
  docReady: PropTypes.bool.isRequired,
};

PdfViewer.defaultProps = {
  numPages: null,
  pageRenderWidth: undefined,
};

export default PdfViewer;
